/**
 * Mock notifications service.
 * Mirrors the real notifications.service.js API with in-memory data.
 * Re-exports groupNotifications, sectionNotifications, and NOTIFICATION_TYPES
 * from the real service since those are pure functions.
 */
import { MOCK_NOTIFICATIONS } from '../../data/mock/mock-notifications.data';
import { mockDelay, mockLog, mockId } from './mock-delay';

// Re-export pure utility functions from the real service
export {
  groupNotifications,
  sectionNotifications,
  NOTIFICATION_TYPES,
} from '../notifications.service';

// In-memory state
let notifications = [...MOCK_NOTIFICATIONS];

const paginate = (items, lastDoc, pageLimit) => {
  let startIndex = 0;
  if (lastDoc) {
    const idx = items.findIndex((n) => n.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }
  const page = items.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < items.length;
  return { page, lastDoc: newLastDoc, hasMore };
};

export const createNotification = async (data) => {
  mockLog('NotificationsService', 'createNotification', data.type);
  await mockDelay(50, 150);
  if (data.recipientId === data.senderId) return;
  const notif = {
    id: `mock_notif_${mockId()}`,
    ...data,
    read: false,
    createdAt: new Date().toISOString(),
    _docSnapshot: null,
  };
  notifications = [notif, ...notifications];
};

export const getNotifications = async (userId, lastDoc = null, pageLimit = 20) => {
  mockLog('NotificationsService', 'getNotifications', { userId, lastDoc, pageLimit });
  await mockDelay();
  const userNotifs = notifications
    .filter((n) => n.recipientId === userId)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(userNotifs, lastDoc, pageLimit);
  return { notifications: page, lastDoc: newLast, hasMore };
};

export const markAsRead = async (notificationId) => {
  mockLog('NotificationsService', 'markAsRead', notificationId);
  await mockDelay(50, 150);
  const idx = notifications.findIndex((n) => n.id === notificationId);
  if (idx !== -1) notifications[idx] = { ...notifications[idx], read: true };
};

export const markAllAsRead = async (userId) => {
  mockLog('NotificationsService', 'markAllAsRead', userId);
  await mockDelay();
  notifications = notifications.map((n) =>
    n.recipientId === userId ? { ...n, read: true } : n
  );
};

export const dismissNotification = async (notificationId) => {
  mockLog('NotificationsService', 'dismissNotification', notificationId);
  await mockDelay(50, 150);
  notifications = notifications.filter((n) => n.id !== notificationId);
};

export const onUnreadCountChange = (userId, callback) => {
  mockLog('NotificationsService', 'onUnreadCountChange', userId);
  const count = notifications.filter((n) => n.recipientId === userId && !n.read).length;
  // Simulate initial callback
  setTimeout(() => callback(count), 100);
  // Return no-op unsubscribe
  return () => {};
};
